/**
 * useScenarios Hook
 *
 * Custom hook for managing scenario save/load operations
 * Handles loading scenarios, saving new scenarios, and applying saved scenarios.
 * Computes a SHA-256 hash of the current settings to detect whether the
 * active configuration already matches a saved scenario.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useSettings } from "../contexts/SettingsContext";
import type { Settings } from "../contexts/SettingsContext";
import { scenarioService } from "../services/scenarioService";
import { useFirestoreScenarios } from "./useFirestoreScenarios";
import { useScenariosUndo } from "./useScenariosUndo";
import { computeSettingsHash } from "../utils/settingsHash";
import type { Scenario, ScenarioPreview } from "../types/scenario";
import { calculateScenarioMetrics } from "../utils/scenarioCalculator";
import type { UndoToastItem } from "../components/UndoToast";

/** Duration in ms before a deferred delete is executed against Firestore */
const DELETE_UNDO_DURATION = 5000;

interface UseScenariosReturn {
  scenarios: (Scenario & { preview?: ScenarioPreview })[];
  scenariosLoading: boolean;
  scenariosError: string | null;
  /** Name of the active scenario (loaded or matched by SHA) */
  activeScenarioName: string | null;
  /** Whether current settings already match a saved scenario */
  isCurrentSettingsSaved: boolean;
  /** Pending deletes awaiting Firestore execution (for undo toast UI) */
  pendingDeletes: UndoToastItem[];
  saveScenario: (name: string, description: string) => Promise<boolean>;
  loadScenario: (scenarioId: string) => Promise<boolean>;
  deleteScenario: (scenarioId: string) => Promise<boolean>;
  /** Cancel a pending delete — re-save the scenario to Firestore */
  undoDelete: (scenarioId: string) => void;
  /** Dismiss a pending delete toast (Firestore delete already completed) */
  confirmDelete: (scenarioId: string) => void;
}

/** Serialise settings to a plain object suitable for hashing */
function serialiseSettings(settings: Settings): Record<string, unknown> {
  return {
    // Use raw values so hashes match saved scenarios that preserve nulls.
    profile: settings.profile.toRawJSON(),
    investableAssets: settings.investableAssets.toRawJSON(),
    realEstateAssets: settings.realEstateAssets.toRawJSON(),
    liabilities: settings.liabilities.toRawJSON(),
    income: settings.income.toRawJSON(),
    expenses: settings.expenses.toRawJSON(),
    assumptions: settings.assumptions.toRawJSON(),
  };
}

export function useScenarios(
  userId: string | undefined,
  maxScenarios: number = 5
): UseScenariosReturn {
  const { settings } = useSettings();

  // Real-time Firestore listener for scenarios
  const {
    scenarios: firestoreScenarios,
    loading: scenariosLoading,
    error: scenariosError,
  } = useFirestoreScenarios(userId ?? "", !!userId);

  // Optimistic state: scenarios added/deleted locally before the Firestore
  // listener confirms them. This avoids UI flicker between save/delete
  // and the next snapshot callback.
  const [optimisticAdds, setOptimisticAdds] = useState<Scenario[]>([]);
  const [optimisticDeletes, setOptimisticDeletes] = useState<Set<string>>(
    () => new Set()
  );

  // Reconcile optimistic state with confirmed Firestore data.
  // Once the listener delivers a snapshot that includes the optimistic add
  // (by id) or no longer includes the optimistic delete, discard it.
  useEffect(() => {
    const fsIds = new Set(firestoreScenarios.map((s) => s.id));
    setOptimisticAdds((prev) => {
      const remaining = prev.filter((s) => !fsIds.has(s.id));
      return remaining.length === prev.length ? prev : remaining;
    });
    setOptimisticDeletes((prev) => {
      const remaining = new Set([...prev].filter((id) => fsIds.has(id)));
      return remaining.size === prev.size ? prev : remaining;
    });
  }, [firestoreScenarios]);

  // Merged scenario list: Firestore truth + optimistic adds − optimistic deletes
  const mergedScenarios = useMemo(() => {
    const base = firestoreScenarios.filter((s) => !optimisticDeletes.has(s.id));
    const fsIds = new Set(firestoreScenarios.map((s) => s.id));
    const pending = optimisticAdds.filter((s) => !fsIds.has(s.id));
    return [...base, ...pending];
  }, [firestoreScenarios, optimisticAdds, optimisticDeletes]);

  // Statistics from SettingsContext (already loaded for user's age)
  const { statisticsMap, statisticsInitialized } = useSettings();

  // Combined scenarios with on-the-fly calculated metrics
  const scenariosWithMetrics = useMemo(() => {
    if (!statisticsInitialized || mergedScenarios.length === 0)
      return mergedScenarios;

    // We also need the user age from SettingsContext via profile settings instance
    if (!settings) return mergedScenarios;
    const currentAge = settings.profile.currentAge;

    return mergedScenarios.map((scenario) => {
      const result = calculateScenarioMetrics(
        scenario.settings,
        currentAge,
        statisticsMap
      );

      return {
        ...scenario,
        preview: {
          retirementAge: scenario.settings.assumptions.retirementAge!,
          finalValue: result.finalAmount,
          monthlyIncome: result.monthlyIncome,
          readinessScore: result.readinessScore,
          shortTermHealthScore: result.shortTermHealthScore,
          liquidReservesScore: result.liquidReservesScore,
          cashFlowMarginScore: result.cashFlowMarginScore,
          lifestyleScore: result.lifestyleScore,
          currency: "CAD",
        },
      };
    });
  }, [mergedScenarios, statisticsMap, statisticsInitialized, settings]);

  // SHA-based comparison state
  const [currentHash, setCurrentHash] = useState<string | null>(null);
  const [scenarioHashes, setScenarioHashes] = useState<Map<string, string>>(
    new Map()
  );
  /** Name of the last scenario explicitly loaded by the user */
  const [loadedScenarioName, setLoadedScenarioName] = useState<string | null>(
    null
  );

  // Compute hash of current settings whenever they change
  useEffect(() => {
    if (!settings) return;
    const plain = serialiseSettings(settings);

    let cancelled = false;
    computeSettingsHash(plain).then((hash) => {
      if (!cancelled) setCurrentHash(hash);
    });
    return () => {
      cancelled = true;
    };
  }, [settings]);

  // Reset current hash when settings are cleared
  const prevHadSettings = currentHash !== null;
  if (!settings && prevHadSettings) {
    setCurrentHash(null);
  }

  // Stable key based on scenario IDs for effect dependencies.
  // The firestoreScenarios array gets a new reference on every Firestore snapshot
  // (or every render with test mocks). Using a content-based key avoids infinite
  // re-render loops caused by reference-equality checks in useEffect deps.
  const scenariosKey = useMemo(
    () => mergedScenarios.map((s) => s.id).join(","),
    [mergedScenarios]
  );

  // Compute hashes for all saved scenarios whenever the list changes
  useEffect(() => {
    let cancelled = false;
    const computeAll = async () => {
      const map = new Map<string, string>();
      for (const scenario of mergedScenarios) {
        const hash = await computeSettingsHash(
          scenario.settings as unknown as Record<string, unknown>
        );
        map.set(hash, scenario.id);
      }
      if (!cancelled) setScenarioHashes(map);
    };
    computeAll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenariosKey]);

  // Determine whether current settings match any saved scenario
  const matchingScenarioId =
    currentHash && scenarioHashes.has(currentHash)
      ? (scenarioHashes.get(currentHash) ?? null)
      : null;
  const matchingScenario = matchingScenarioId
    ? (mergedScenarios.find((s) => s.id === matchingScenarioId) ?? null)
    : null;

  const isCurrentSettingsSaved = matchingScenarioId !== null;
  const activeScenarioName = matchingScenario
    ? matchingScenario.name
    : loadedScenarioName;

  // Convert hook error to string format
  const formattedError = useMemo(() => {
    if (!scenariosError) return null;
    if (scenariosError instanceof Error) return scenariosError.message;
    if (typeof scenariosError === "string") return scenariosError;
    return "Failed to load scenarios";
  }, [scenariosError]);

  // Save scenario
  const saveScenario = useCallback(
    async (name: string, description: string): Promise<boolean> => {
      if (!userId || !settings) return false;

      // Use toRawJSON() to preserve null values instead of toJSON() which fills in defaults
      const response = await scenarioService.saveScenario({
        userId,
        name,
        description,
        maxScenarios,
        settings: {
          profile: settings.profile.toRawJSON(),
          investableAssets: settings.investableAssets.toRawJSON(),
          realEstateAssets: settings.realEstateAssets.toRawJSON(),
          liabilities: settings.liabilities.toRawJSON(),
          income: settings.income.toRawJSON(),
          expenses: settings.expenses.toRawJSON(),
          assumptions: settings.assumptions.toRawJSON(),
        },
      });

      if (response.ok) {
        // Optimistically add so the UI updates immediately.
        // The reconciliation effect will discard once the listener confirms.
        setOptimisticAdds((prev) => [...prev, response.value]);
        setLoadedScenarioName(name);
        return true;
      } else {
        return false;
      }
    },
    [userId, settings, maxScenarios]
  );

  // Load scenario and apply its settings
  const loadScenario = useCallback(
    async (scenarioId: string): Promise<boolean> => {
      if (!userId) return false;

      let scenario =
        mergedScenarios.find((existing) => existing.id === scenarioId) ?? null;

      if (!scenario) {
        const getResponse = await scenarioService.getScenario(
          userId,
          scenarioId
        );

        if (!getResponse.ok) {
          return false;
        }

        scenario = getResponse.value;
        if (!scenario) {
          return false;
        }
      }

      const applyResponse = await scenarioService.applyScenarioSettings(
        userId,
        scenario
      );

      if (applyResponse.ok) {
        setLoadedScenarioName(scenario.name);
        return true;
      } else {
        return false;
      }
    },
    [userId, mergedScenarios]
  );

  // ------------------------------------------------------------------
  // Undo-able delete: delegated to useScenariosUndo
  // ------------------------------------------------------------------
  const handleFirestoreDelete = useCallback(
    async (scenarioId: string): Promise<boolean> => {
      if (!userId) return false;

      // Optimistically remove so the UI updates immediately
      setOptimisticDeletes((prev) => new Set([...prev, scenarioId]));
      setOptimisticAdds((prev) => prev.filter((s) => s.id !== scenarioId));

      // Clear loaded scenario name if the deleted scenario was active
      if (loadedScenarioName) {
        const deleted = mergedScenarios.find((s) => s.id === scenarioId);
        if (deleted && deleted.name === loadedScenarioName) {
          setLoadedScenarioName(null);
        }
      }

      const response = await scenarioService.deleteScenario(userId, scenarioId);
      if (!response.ok) {
        // Restore on failure
        setOptimisticDeletes((prev) => {
          const next = new Set(prev);
          next.delete(scenarioId);
          return next;
        });
        return false;
      }
      return true;
    },
    [userId, mergedScenarios, loadedScenarioName]
  );

  const handleUndoRestore = useCallback(
    (cached: Scenario) => {
      if (!userId) return;
      scenarioService
        .saveScenario({
          userId,
          name: cached.name,
          description: cached.description ?? "",
          settings: cached.settings,
          maxScenarios,
        })
        .then((response) => {
          if (response.ok) {
            setOptimisticAdds((prev) => [...prev, response.value]);
            setLoadedScenarioName(cached.name);
          }
        });
    },
    [userId, maxScenarios]
  );

  const { pendingDeletes, requestDelete, undoDelete, confirmDelete } =
    useScenariosUndo({
      undoDuration: DELETE_UNDO_DURATION,
      onDelete: handleFirestoreDelete,
      onRestore: handleUndoRestore,
    });

  // Delete scenario — wraps requestDelete with scenario lookup
  const deleteScenario = useCallback(
    async (scenarioId: string): Promise<boolean> => {
      const scenario = mergedScenarios.find((s) => s.id === scenarioId);
      if (!scenario) return false;
      return requestDelete(scenario);
    },
    [mergedScenarios, requestDelete]
  );

  return {
    scenarios: scenariosWithMetrics,
    scenariosLoading,
    scenariosError: formattedError,
    activeScenarioName,
    isCurrentSettingsSaved,
    pendingDeletes,
    saveScenario,
    loadScenario,
    deleteScenario,
    undoDelete,
    confirmDelete,
  };
}
